<template>
  <div id="app">
    <nav class="navigation" v-if="app.isLoggedIn">
      <ul>
        <li class="actions-menu">
          <v-popover class="menu" :offset="[90, 15]" ref="popoverRef">
            <template v-slot:default>
              <button class="icon darker large menu-button">
                <v-icon name="settings" />
              </button>
            </template>
            <template v-slot:popover>
              <ul>
                <li class="logo">
                  <v-logo href="/"></v-logo>
                </li>
                <li>
                  <button class="anchor" @click="modal.show('connection')">
                    <v-icon name="server" />
                    {{ $t('Connection') }}
                  </button>
                </li>
                <li>
                  <button class="anchor" @click="modal.show('devices')">
                    <v-icon name="plus" />
                    {{ $t('Add Devices') }}
                  </button>
                </li>
                <li>
                  <button class="anchor" @click="createNewConfiguration">
                    <v-icon name="plus" />
                    {{ $t('New Configuration') }}
                  </button>
                </li>
                <li>
                  <v-file class="icon anchor" accept="application/xml" @change="loadConfiguration">
                    <v-icon name="upload-cloud" />
                    {{ $t('Load Configuration') }}
                  </v-file>
                </li>
                <li>
                  <button class="anchor" @click="downloadConfiguration" :disabled="!ethercat.hasDomains">
                    <v-icon name="download-cloud" />
                    {{ $t('Download Configuration') }}
                  </button>
                </li>
                <li>
                  <button class="anchor" @click="modal.show('deploy')">
                    <v-icon name="code" />
                    {{ $t('Preview & Deploy') }}
                  </button>
                </li>
                <li>
                  <button class="anchor" @click="modal.show('settings')">
                    <v-icon name="settings" />
                    {{ $t('Settings') }}
                  </button>
                </li>
                <li>
                  <v-toggle-darkmode darkmode></v-toggle-darkmode>
                </li>
                <li v-if="isDebug">
                  <router-link to="/testing">
                    <v-icon name="aperture" />
                    {{ $t('Tests') }}
                  </router-link>
                </li>
                <li>
                  <div class="version">
                    <strong>{{ $t('Version') }}</strong>
                    <br />
                    {{ version }}
                  </div>
                </li>
              </ul>
            </template>
          </v-popover>
          <div class="me">
            <Me></Me>
          </div>
        </li>
      </ul>

      <ul class="actions">
        <li>
          <v-promise-button class="darker" @click="fetchConfiguration" :disabled="!app.canScan">
            <template slot="loading">
              <v-loader></v-loader>
              {{ $t('Fetching') }}
            </template>
            <template slot="success">
              <v-icon name="check" />
              {{ $t('Completed') }}
            </template>
            <template slot="error">
              <v-icon name="x" />
              {{ $t('Failed') }}
            </template>
            <v-icon name="upload" />
            {{ $t('Fetch') }}
          </v-promise-button>
        </li>
        <li>
          <v-deploy :disabled="!app.canScan" />
        </li>
        <li class="connection-indicator" :class="{ 'is-connected': app.isConnected }" @click="modal.show('connection')">
          <v-loader v-if="app.isConnecting"></v-loader>
          <span v-else></span>
          <v-icon name="lock" v-if="app.hasSecureConnection" />
          <v-icon name="unlock" v-else />
        </li>
      </ul>
    </nav>

    <router-view />
    <template v-if="app.isLoggedIn">
      <v-flits />
      <v-dialog />
      <v-modal-deploy />
      <v-modal-devices />
      <v-modal-parameters />
      <v-modal-connection />
      <v-modal-settings />
    </template>
  </div>
</template>
<script lang="ts" setup>
import Me from './me/index.vue'
import { $t } from '/@/i18n'
import { useStore } from '/@/shared/composables/store'
import { useApp } from '/@/shared/composables/app'
import { useModal, useDialog, useFlits } from '@vectioneer/ui'
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router/composables'
import ScanAdapter from '/@/shared/adapters/configuration/ScanAdapter'
import FileAdapter from '/@/shared/adapters/configuration/FileAdapter'
import { wait } from '/@/shared/utils'

const { app, ethercat } = useStore()
const { isDebug, version } = useApp()
const modal = useModal()
const dialog = useDialog()
const router = useRouter()
const route = useRoute()
const flits = useFlits()
const popoverRef = ref<any>(null)

const checkIfShouldApplyConfiguration = async () => {
  if (ethercat.hasDevices) {
    const confirmed = await dialog
      .okText('Yes')
      .cancelText('No')
      .confirm('This action will replace your current configuration, are you sure you want to proceed?')

    return confirmed
  }

  return true
}

const createNewConfiguration = async () => {
  if (await checkIfShouldApplyConfiguration()) {
    ethercat.clearState(false)
  }
}

const fetchConfiguration = async () => {
  if (await checkIfShouldApplyConfiguration()) {
    try {
      if (!app.canScan) {
        flits.error('Not connected to a device')
        return
      }

      const domains = await new ScanAdapter().process(app.connectionParameters.host)

      ethercat.applyConfiguration({
        domains,
      })

      redirectToRoot()
    } catch (error) {
      flits.error(error)
    }
  }
}

const loadConfiguration = async (event: InputEvent) => {
  if (!(await checkIfShouldApplyConfiguration())) {
    return
  }

  const target = event.target as HTMLInputElement
  const file = (target.files as FileList).item(0)

  if (!file) {
    return
  }

  try {
    const domains = await new FileAdapter().process(file)

    ethercat.applyConfiguration({
      domains,
    })

    popoverRef.value.hide()
  } catch (error) {
    flits.error(error)
    throw error
  }
}

const downloadConfiguration = async () => {
  await ethercat.downloadConfiguration()
  await wait(200)
  popoverRef.value.hide()
}

const redirectToRoot = () => {
  if (route.path !== '/') {
    router.push('/')
  }
}

app.loadControllerFeatures()
</script>
<style src="./style.css"></style>
